import React, { useContext } from 'react';
import { useNavigate } from 'react-router';
import * as XLSX from 'xlsx';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import useDownloadDriverTemplate from '../../../../hooks/dashboard/driver-setup/useDownloadDriverTemplate';
import useUploadDriversListExcel from '../../../../hooks/dashboard/driver-setup/useUploadDriversListExcel';
import {
  SetUpADriverAccountFromExcelInitialValues,
  SetUpADriverAccountFromExcelSchema,
} from '../../../../models/dashboard/driver-setup/setup-drivers-account-from-excel';
import { DRIVER_LIST } from '../../../../routes/routeConstants';
import CancelCsvUploadFormSubmitButton from '../../../Button/CancelCsvUploadFormSubmitButton';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import ResetFormSubmitButton from '../../../Button/ResetFormSubmitButton';
import { FormComponent } from '../../../FormCom/FormComponent';
import UploadExcelFileField from '../../../FormCom/UploadExcelFileField/UploadExcelFileField';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';
import PlainSuccessModal from '../../PlainSuccessModal';

interface UploadExcelFileModalProps {
  // loading: boolean;
  // newlyGeneratedApiKey?: string;
}

const UploadExcelFileModal: React.FC<UploadExcelFileModalProps> = () => {
  const modalContext = useContext(ModalContext);
  const navigate = useNavigate();
  const { uploadDriversListExcel, loading } = useUploadDriversListExcel();
  const { downloadDriverTemplate, loading: loadingDownload } =
    useDownloadDriverTemplate();

  const getRowCount = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          if (!worksheet) {
            resolve(0);
            return;
          }

          const rows = XLSX.utils.sheet_to_json<(string | number | boolean)[]>(
            worksheet,
            {
              header: 1,
              defval: '',
              blankrows: false,
            },
          );

          const dataRows = rows
            .slice(1)
            .filter((row) =>
              row.some((cell) => String(cell ?? '').trim() !== ''),
            );

          resolve(dataRows.length);
        } catch (error) {
          console.error('Failed to count rows from uploaded file:', error);
          resolve(0);
        }
      };

      reader.onerror = () => resolve(0);
      reader.readAsArrayBuffer(file);
    });
  };

  const successActionFn = () => {
    modalContext?.setShowModal(false);
    navigate(`/${DRIVER_LIST}`);
  };

  const onSubmit = async (value: any, actions: any) => {
    let count = 0;
    if (value.excelFile && value.excelFile[0]) {
      count = await getRowCount(value.excelFile[0]);
    }
    modalContext?.setSuccessDescription(
      `${count} driver account${count === 1 ? '' : 's'} ${
        count === 1 ? 'has' : 'have'
      } been set up.`,
    );
    uploadDriversListExcel(value, () => actions.resetForm());
  };

  const onCancel = () => {
    modalContext?.setShowModal(false);
  };

  const onDownloadTemplate = () => {
    downloadDriverTemplate();
  };

  return (
    <ModalContainer
      width={!modalContext?.showSuccessComponent ? '867px' : undefined}
      label='Upload Excel File'
      description='New Drivers accounts have been set up.'
      successComponent={() => (
        <PlainSuccessModal
          successActionFn={successActionFn}
          description={
            modalContext?.successDescription ||
            'New Driver accounts have been set up.'
          }
          btnLabel='View Driver List'
          hasCancelBtn={true}
        />
      )}
      showSuccessComponent={modalContext?.showSuccessComponent}
    >
      <FormComponent
        initialValues={SetUpADriverAccountFromExcelInitialValues}
        schema={SetUpADriverAccountFromExcelSchema}
        onSubmit={onSubmit}
        className={'small-gap'}
      >
        <CsvViewerContainer>
          <UploadExcelFileField
            label='Driver Credentials File'
            name='excelFile'
            id='excelFile'
            placeholder='Drag and drop your CSV or Excel file here'
          />
        </CsvViewerContainer>

        <DownloadTemplate onClick={onDownloadTemplate}>
          Download Template {loadingDownload && <LoadingOutlined />}
        </DownloadTemplate>

        <CancelCsvUploadFormSubmitButton
          className='clear large cancel'
          onClick={onCancel}
        >
          Cancel
        </CancelCsvUploadFormSubmitButton>

        <FormButtonArea>
          <ResetFormSubmitButton className='clear large' detachMode={true}>
            Cancel
          </ResetFormSubmitButton>
          <FormSubmitButton
            className='blue large'
            loading={loading}
            detachMode={true}
          >
            Generate Driver Profiles
          </FormSubmitButton>
        </FormButtonArea>
      </FormComponent>
    </ModalContainer>
  );
};

export default UploadExcelFileModal;

const FormButtonArea = styled.div`
  margin-top: 32px;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const DownloadTemplate = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-top: 0px;
  color: #1f2130;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CsvViewerContainer = styled.div`
  padding: 16px;
  background-color: #fafafa;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;

  overflow-x: auto;
`;
